/**
 * The signed-in gardener's preferences (theme, language, text size,
 * hemisphere, units, Coming up's days): loaded from their account, applied to
 * the page, and saved back.
 *
 * The theme last applied is also kept on the device (themeUtils) so the first
 * paint, before the account answers, is already in the right colours. Before
 * there is an account the language is the start language (the browser's, or
 * the one chosen on the sign-in page; startLanguage), never reset to English.
 *
 * The config also carries the garden's `city` and `state` (two letters), read
 * from the Grow account (mysqlUser.city, state_province), which Today shows as
 * "Fall almanac · Sparta, TN".
 *
 * Reads GET /user-settings/preferences (api/preferences) once per Grow
 * account (keyed on its id, so a change to the account's other fields does
 * not read them again; B24: a Today load read them three times), and saves
 * the whole set with PUT; held in React state, not
 * React Query. A failed read says so in a toast (for a failure on our side,
 * the standard sentence, reported to Sentry: lib/serverFailure); a failed
 * save is thrown for the screen that made it to say. Applying (utils/themeUtils
 * applyPreferences) sets the theme class, the root font size and the
 * language (i18n setLanguage). The root font size is the Grow settings'
 * (ui.ROOT_FONT_*_PX, lib/growSettings), so it is applied again when their
 * answer arrives (onGrowSettings).
 *
 * Mounted once by App around the signed-in pages (inside AuthProvider,
 * outside ThemeProvider); read with useUserConfig (userConfigStore).
 *
 * @module UserConfigProvider
 */
import { t } from '@/shared/i18n/i18nSetup';
import { ReactNode, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { UserConfig, DEFAULT_USER_CONFIG, type Preferences } from '@/config/config';
import { toast } from '@/shared/components/ui/use-toast';
import { getUserPreferences, updateUserPreferences } from '@/shared/api/preferences';
import { useAuth } from '@/shared/hooks/useAuth';
import { applyFontSize, applyPreferences, getSavedTheme, normaliseTheme } from '@/shared/utils/themeUtils';
import { onGrowSettings } from '@/shared/lib/growSettings';
import { startLanguage } from '@/shared/lib/startLanguage';
import { serverFailureText } from '@/shared/lib/serverFailure';
import { UserConfigContext } from './userConfigStore';
import { debugLog } from '@/config/config';

/**
 * Props for the UserConfigProvider component
 */
interface UserConfigProviderProps {
  /** Child components that will have access to the user config context */
  children: ReactNode;
}

/**
 * Provider component that manages user configuration state and preferences
 * 
 * This component:
 * - Manages theme and other user preferences
 * - Keeps the theme on the device (through applyPreferences) for the next first paint
 * - Handles loading/saving preferences from/to the backend
 * - Provides context for user configuration to child components
 * 
 * @param props - The component props
 * @param props.children - Child components that will have access to the user config context
 * @returns A provider component that wraps its children with user configuration context
 */
export function UserConfigProvider({ children }: UserConfigProviderProps): JSX.Element {
  // Initialize with saved theme if available
  const savedTheme = getSavedTheme();
  const initialConfig: UserConfig = {
    ...DEFAULT_USER_CONFIG,
    preferences: {
      ...DEFAULT_USER_CONFIG.preferences,
      theme: savedTheme || DEFAULT_USER_CONFIG.preferences.theme
    }
  };
  
  const [userConfig, setUserConfig] = useState<UserConfig>(initialConfig);
  const { mysqlUser } = useAuth();
  // The account the preferences belong to: the read is keyed on it, not on the account object.
  const accountId = mysqlUser?.user_id ?? null;
  // The account signed in now, for dropping an answer that arrives after a change of account.
  const currentAccount = useRef<number | null>(accountId);
  currentAccount.current = accountId;

  /**
   * Refreshes user preferences from the backend and applies them. Never
   * throws: a failed read is said in a toast and the page keeps what it has.
   * An answer that arrives after the account has changed is dropped, and
   * with no account there is nothing to read.
   *
   * @returns Once the preferences are applied, or the failure said.
   */
  const refreshPreferences = useCallback(async (): Promise<void> => {
    const askedFor = currentAccount.current;
    if (askedFor === null) return;
    try {
      const fetched = await getUserPreferences(askedFor);
      if (currentAccount.current !== askedFor) return;
      // Saved under a retired name? It is Grow now, and the switcher should
      // say so rather than show nothing ticked.
      const preferences = { ...fetched, theme: normaliseTheme(fetched.theme) };
      debugLog('UserConfig', 'Fetched preferences:', preferences);
      setUserConfig(prev => ({
        ...prev,
        preferences
      }));
      applyPreferences(preferences);
    } catch (error) {
      // The account changed meanwhile: the failure was the last gardener's.
      if (currentAccount.current !== askedFor) return;
      // On our side: what failed, then the standard sentence (reported there);
      // the request limit (a 429): its own sentence (serverFailureText, B24).
      const standard = serverFailureText(error, 'preferences');
      if (!standard) console.error('Error fetching preferences:', error);
      toast({
        title: standard ? t('profile.preferences.toast.loadFailed') : t('profile.preferences.toast.error'),
        description: standard ?? t('profile.preferences.toast.loadFailed'),
        variant: "destructive"
      });
    }
  }, []);

  // Load preferences when the account changes: keyed on its id, so the
  // account's other fields changing (a ZIP saved, a photo) or the theme kept
  // on the device do not read them again (B24).
  //
  // Keyed on the Grow account, not the Firebase one. Those are not the same
  // thing: between signing in and finishing signup a person has a verified
  // Firebase identity and no row in the user table, and this provider sits
  // above Layout, so it runs on the login and signup screens too. Asking for
  // preferences there sent a request that verifyAuth answers 404 — no
  // account, no preferences — and put "Failed to load preferences" in red
  // over the first screen a new gardener ever sees.
  //
  // Preferences belong to an account. Until there is one there is nothing to
  // fetch, and the saved theme below is the right answer anyway.
  //
  // Not keyed on the theme. It used to be, for a debug line, so every theme
  // change asked the server for the preferences again — racing the save that
  // updateUserConfig had just started, which could answer with the old theme
  // and flick the page back to it.
  useEffect(() => {
    if (accountId === null) return;
    debugLog('UserConfig', 'UserConfigProvider: Loading preferences', { accountId });
    void refreshPreferences();
  }, [accountId, refreshPreferences]);

  // No account yet: the theme kept on the device, in the start language.
  useEffect(() => {
    if (accountId !== null || !savedTheme) return;
    debugLog('UserConfig', 'UserConfigProvider: Applying saved theme', savedTheme);
    // In the start language: the defaults' English would undo a Spanish browser's.
    applyPreferences({
      ...DEFAULT_USER_CONFIG.preferences,
      theme: savedTheme,
      language: startLanguage(),
    });
  }, [accountId, savedTheme]);

  /**
   * Updates the user configuration state and applies the new preferences at
   * once. Only a changed theme is saved (quietly, in the background); other
   * preferences changed this way are not saved: savePreferences is for those.
   *
   * @param newConfig - The new user configuration to apply
   */
  const updateUserConfig = useCallback((newConfig: UserConfig) => {
    debugLog('UserConfig', 'UserConfigProvider: Updating config', {
      oldTheme: userConfig.preferences.theme,
      newTheme: newConfig.preferences.theme
    });
    setUserConfig(newConfig);
    applyPreferences(newConfig.preferences);

    // If theme changed, persist it silently
    if (newConfig.preferences.theme !== userConfig.preferences.theme) {
      updateUserPreferences(newConfig.preferences).catch(error => {
        console.error('Error saving theme preference:', error);
      });
    }
  }, [userConfig.preferences.theme]);

  // The root text size is the Grow settings' (ui.ROOT_FONT_*_PX): when their answer arrives, the chosen size is applied again.
  const chosenFontSize = userConfig.preferences.fontSize;
  useEffect(() => onGrowSettings(() => applyFontSize(chosenFontSize)), [chosenFontSize]);

  /**
   * Saves user preferences to the backend and updates the local state with
   * what the server kept.
   *
   * @param preferences - The preferences to save, whole.
   * @throws Will throw an error if the preferences cannot be saved
   */
  const savePreferences = useCallback(async (preferences: Preferences) => {
    try {
      const updatedPreferences = await updateUserPreferences(preferences);
      setUserConfig(prev => ({
        ...prev,
        preferences: updatedPreferences
      }));
      applyPreferences(updatedPreferences);
    } catch (error) {
      // Said where the choice was made, beside it, by the caller; a toast
      // across the screen for some choices and silence for others was the
      // profile's old way.
      console.error('Error saving preferences:', error);
      throw error;
    }
  }, []);

  // The town and state come from the account (mysqlUser, which the profile
  // and Today's ZIP box update when they save), so "Sparta, TN" is there as
  // soon as a ZIP is saved, with no read of its own.
  const city = mysqlUser?.city?.trim() || null;
  const state = mysqlUser?.state_province?.trim() || null;

  // One object per change, not per render: every useUserConfig() reader
  // re-rendered whenever anything above this provider did.
  const value = useMemo(() => ({
    userConfig: { ...userConfig, city, state },
    updateUserConfig,
    savePreferences,
    refreshPreferences
  }), [userConfig, city, state, updateUserConfig, savePreferences, refreshPreferences]);

  return (
    <UserConfigContext.Provider value={value}>
      {children}
    </UserConfigContext.Provider>
  );
} 